/**
 * Liga o reporter ao navegador: erros globais, rejeições não tratadas e a
 * camada de rede. Um módulo só, chamado uma vez em `main.tsx`.
 *
 * A rede é observada envolvendo `window.fetch` — o app não tem um cliente HTTP
 * central (cada feature chama `fetch` com `credentials: 'same-origin'`), e
 * trocar 35 arquivos por causa de diagnóstico seria churn sem ganho. O
 * invólucro só olha chamadas para `/api/` desta origem, e:
 *
 *   - lê `x-server-release` de TODA resposta (é assim que o cliente sabe qual
 *     build do servidor viu por último — sem polling novo);
 *   - em 5xx captura um incidente com o `x-request-id` da resposta;
 *   - em falha de rede COM `navigator.onLine` captura `network_failure`; sem
 *     rede não é incidente (o offline é comportamento válido do produto);
 *   - ignora a própria rota de diagnóstico (`x-nv-diagnostico`): erro ao
 *     enviar diagnóstico nunca vira outro diagnóstico;
 *   - NUNCA lê corpo de requisição ou de resposta.
 *
 * 4xx não passa por aqui de propósito (`classificarHttp`): 401 da sessão,
 * 403, 404 que esconde recurso, 409 de conflito, 422 de validação e 429 são o
 * domínio funcionando.
 */
import { classificarHttp, rotaGenerica } from '@nv/core';

import { DiagnosticReporter, transporteHttp } from './reporter';

const MARCA = Symbol.for('nv.diagnostico.capturado');

function marcar(erro: unknown): void {
  if (erro && typeof erro === 'object') {
    try {
      Object.defineProperty(erro, MARCA, { value: true, enumerable: false });
    } catch {
      /* objeto congelado */
    }
  }
}

/** Erro já contabilizado pela camada de rede (ou pelo reporter) — não conta de novo. */
export function jaCapturado(erro: unknown): boolean {
  return !!erro && typeof erro === 'object' && (erro as Record<symbol, unknown>)[MARCA] === true;
}

let instancia: DiagnosticReporter | null = null;

/** O reporter da aplicação. Antes de `instalarObservabilidade`, um inerte. */
export function reporter(): DiagnosticReporter {
  if (!instancia) {
    instancia = new DiagnosticReporter({ usuarioAtual: () => null, transporte: async () => ({ status: 0 }), marcar });
  }
  return instancia;
}

export interface OpcoesDeInstalacao {
  usuarioAtual: () => string | null;
  /** Para testes; padrão: `window`. */
  janela?: Window & typeof globalThis;
}

export function instalarObservabilidade(opts: OpcoesDeInstalacao): DiagnosticReporter {
  const w = opts.janela ?? window;
  instancia = new DiagnosticReporter({ usuarioAtual: opts.usuarioAtual, transporte: transporteHttp, marcar });
  const rep = instancia;

  w.addEventListener('error', (ev) => {
    try {
      if (jaCapturado(ev.error)) return;
      // Sem `ev.message`: é texto livre; a regra estrutural descarta a mensagem
      // de fonte não controlada, e o que fica é nome + frames do erro.
      rep.capturar({ source: 'window_error', error: ev.error });
    } catch {
      /* nunca */
    }
  });

  w.addEventListener('unhandledrejection', (ev) => {
    try {
      if (jaCapturado(ev.reason)) return;
      rep.capturar({ source: 'unhandled_rejection', error: ev.reason });
    } catch {
      /* nunca */
    }
  });

  w.addEventListener('online', () => void rep.enviarPendentes());
  // Na abertura, o que ficou de uma sessão anterior sem rede.
  w.setTimeout(() => void rep.enviarPendentes(), 5000);

  instrumentarFetch(w, rep);
  return rep;
}

function caminhoDaApi(entrada: RequestInfo | URL, w: Window): string | null {
  try {
    const bruto = typeof entrada === 'string' ? entrada : entrada instanceof URL ? entrada.href : entrada.url;
    const url = new URL(bruto, w.location.href);
    if (url.origin !== w.location.origin) return null;
    return url.pathname.startsWith('/api/') ? url.pathname : null;
  } catch {
    return null;
  }
}

function temCabecalho(init: RequestInit | undefined, entrada: RequestInfo | URL, nome: string): boolean {
  try {
    const h = init?.headers ?? (entrada instanceof Request ? entrada.headers : undefined);
    if (!h) return false;
    if (h instanceof Headers) return h.has(nome);
    if (Array.isArray(h)) return h.some(([k]) => k.toLowerCase() === nome);
    return Object.keys(h).some((k) => k.toLowerCase() === nome);
  } catch {
    return false;
  }
}

export function instrumentarFetch(w: Window & typeof globalThis, rep: DiagnosticReporter): void {
  const original = w.fetch.bind(w);
  const instrumentado: typeof w.fetch = async (entrada, init) => {
    const caminho = caminhoDaApi(entrada, w);
    if (!caminho || temCabecalho(init, entrada, 'x-nv-diagnostico')) return original(entrada, init);
    const metodo = (init?.method ?? (entrada instanceof Request ? entrada.method : 'GET')).toUpperCase();
    // O caminho da API entra genérico (sem ids): serve para dizer QUAL rota falhou, não qual viagem.
    const rotaDaApi = rotaGenerica(caminho) ?? '/api/…';
    let res: Response;
    try {
      res = await original(entrada, init);
    } catch (erro) {
      try {
        if (classificarHttp(null, w.navigator.onLine) === 'incidente') {
          rep.capturar({ source: 'network_failure', error: erro, message: `${metodo} ${rotaDaApi}: falha de rede`, route: w.location.pathname });
        }
      } catch {
        /* nunca */
      }
      throw erro;
    }
    try {
      rep.registrarServerRelease(res.headers.get('x-server-release'));
      if (classificarHttp(res.status, w.navigator.onLine) === 'incidente') {
        const requestId = res.headers.get('x-request-id');
        rep.registrarRequestIdComFalha(requestId);
        rep.capturar({
          source: 'http_5xx',
          message: `${metodo} ${rotaDaApi} → ${res.status}`,
          httpStatus: res.status,
          ...(requestId ? { requestId } : {}),
          route: w.location.pathname,
        });
      }
    } catch {
      /* nunca */
    }
    return res;
  };
  w.fetch = instrumentado;
}
